<template>
  <div class="projet-part">
    <h2 id="participants" class="pdf-page-break">
      {{ $t("projetPage.infoAdministratives.participants.titre") }}
    </h2>
    <fieldset>
      <legend>{{ $t("projetPage.infoAdministratives.classe.titre") }}</legend>
      <div>
        <!-- Hidden input to trigger validation issue if missing a class -->
        <div v-if="validationActivated" class="is-mandatory">{{ $t("obligatoire") }}</div>
        <b-message v-if="validationActivated && projetAnnuel.classes.length == 0" type="is-danger">
          {{ $t("projetPage.infoAdministratives.classe.no-class-warning") }}
        </b-message>
        <!-- Champ de validation toujours présent mais invisible - nécessaire pour VeeValidate -->
        <div style="position: absolute; left: -9999px; width: 1px; height: 1px; overflow: hidden">
          <InputWithValidation
            v-if="projetAnnuel.classes.length == 0"
            vid="participants_classes_missing"
            rules="required"
            :value="''"
          />
        </div>
        <ProjetClassesView
          :is-porteur-projet="isPorteurProjet"
          :projet-annuel="projetAnnuel"
          :readonly="readonly"
          @dirtify-project="dirtifyProject"
          @projet-annuel-updated="updateProjetAnnuel"
          @trigger-validation="$emit('trigger-validation')"
        />
      </div>
    </fieldset>
    <fieldset>
      <legend>{{ $t("projetPage.infoAdministratives.participants.titreEnseignant") }}</legend>
      <div>
        <ProjetEnseignantsView
          :projet-annuel="projetAnnuel"
          :invitations="invitations"
          :readonly="readonly"
          :show-invite-link="showInviteLink()"
          :is-porteur-projet="isPorteurProjet"
          @dirtify-project="dirtifyProject"
          @invite-enseignant="inviteEnseignant"
          @invite-to-join-enseignant="inviteToJoinEnseignant"
          @update-enseignants="updateEnseignants"
          @update-consentement-en-attente="updateConsentementEnAttente"
          @open-profile-modale="openProfileModale"
          @trigger-validation="$emit('trigger-validation')"
        />
      </div>
    </fieldset>
    <fieldset>
      <legend>{{ $t("projetPage.infoAdministratives.participants.titreReferent") }}</legend>
      <div>
        <ProjetReferentsView
          :projet-annuel="projetAnnuel"
          :invitations="invitations"
          :readonly="readonly && projetAnnuel.statut !== 'ACCEPTE_SOUS_RESERVE_REFERENT'"
          :show-invite-link="
            (showInviteLink() || projetAnnuel.statut === 'ACCEPTE_SOUS_RESERVE_REFERENT') &&
            !usersService.isObservateurNational(loggedUser)
          "
          :is-porteur-projet="isPorteurProjet"
          @dirtify-project="dirtifyProject"
          @invite-referent="inviteReferent"
          @invite-to-join-referent="inviteToJoinReferent"
          @projet-annuel-updated="updateProjetAnnuel"
          @open-profile-modale="openProfileModale"
          @trigger-validation="$emit('trigger-validation')"
        />
      </div>
    </fieldset>
    <fieldset>
      <legend>{{ $t("projetPage.infoAdministratives.participants.titreActeurEducNat") }}</legend>
      <div>
        <div class="mt-3 mb-3">
          <i>
            {{ $t("projetPage.infoAdministratives.participants.acteurEducNatDescription") }}
          </i>
        </div>
        <InputWithValidation
          type="textarea"
          vid="participants_acteurs_educ_nat"
          :model-value="projetAnnuel.acteursEducNationaleText"
          maxlength="1400"
          :show-validation-errors="showValidationErrors"
          :disabled="readonly || !isPorteurProjet"
          @update:model-value="updateActeurseducnationaletext"
          @manual-change="dirtifyProject(false)"
        />
        <div class="mt-3 mb-3">
          <i>
            {{ $t("projetPage.infoAdministratives.participants.autreActeurDescription") }}
          </i>
        </div>
        <InputWithValidation
          type="textarea"
          vid="participants_autres_acteurs"
          :model-value="projetAnnuel.autresActeursText"
          maxlength="1400"
          :disabled="readonly || !isPorteurProjet"
          :show-validation-errors="showValidationErrors"
          @update:model-value="updateAutresacteurstext"
          @manual-change="dirtifyProject(false)"
        />
      </div>
    </fieldset>
  </div>
</template>

<script lang="ts">
import type { ProfilUtilisateur } from "@/model/bean/GeneratedDTOs"
import { ProjetAnnuelDTO, UtilisateurDTO } from "@/model/bean/GeneratedDTOs"
import { PROFIL_UTILISATEUR } from "@/model/bean/EnumHelper"
import { defineComponent } from "vue"
import ProjetEnseignantsView from "@/pages/projet-page/info-administratives/ProjetEnseignantsView.vue"
import ProjetReferentsView from "@/pages/projet-page/info-administratives/ProjetReferentsView.vue"
import ProjetClassesView from "@/pages/projet-page/info-administratives/ProjetClassesView.vue"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import { LoginService } from "@/services/rest/login/LoginService"
import { UsersService } from "@/services/rest/users/UsersService"
import InputWithValidation from "@/views/inputs/InputWithValidation.vue"
import { eventBus } from "@/services/EventBus"
import { BMessage } from "buefy"

export default defineComponent({
  name: "ProjetParticipants",
  components: {
    ProjetEnseignantsView,
    ProjetReferentsView,
    ProjetClassesView,
    InputWithValidation,
    BMessage,
  },
  props: {
    projetAnnuel: {
      type: Object as () => ProjetAnnuelDTO,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    isPorteurProjet: {
      type: Boolean,
      default: false,
    },
    validationActivated: {
      type: Boolean,
      default: true,
    },
    showValidationErrors: {
      type: Boolean,
      default: true,
    },
  },

  emits: ["trigger-validation", "projet-annuel-updated", "dirtify-project"],
  data() {
    return {
      loginService: LoginService.INSTANCE,
      usersService: UsersService.INSTANCE,
      infoReportingService: InfoReportingService.INSTANCE,
      loggedUser: new UtilisateurDTO(),
      invitations: [] as string[],
    }
  },
  created() {
    const loggedUser = this.loginService.getLoggedUser()
    if (!loggedUser || !loggedUser.id) {
      return
    }
    this.loggedUser = loggedUser
    eventBus.on("project-saved", () => {
      this.invitations = []
    })
  },
  methods: {
    updateAutresacteurstext(newValue: any): void {
      const updatedProjetannuel = {
        ...this.projetAnnuel,
        autresActeursText: newValue,
      }
      this.$emit("projet-annuel-updated", updatedProjetannuel)
      this.$emit("dirtify-project")
    },
    updateActeurseducnationaletext(newValue: any): void {
      const updatedProjetannuel = {
        ...this.projetAnnuel,
        acteursEducNationaleText: newValue,
      }
      this.$emit("projet-annuel-updated", updatedProjetannuel)
      this.$emit("dirtify-project")
    },
    showInviteLink(): boolean {
      return (
        (this.isPorteurProjet ||
          this.usersService.isAnimateurRegional(this.loggedUser) ||
          this.usersService.isAccompagnateurNationalOrAdmin(this.loggedUser)) &&
        !this.readonly
      )
    },

    dirtifyProject(withRedirect = false): void {
      this.$emit("dirtify-project", withRedirect)
    },

    isValidEmail(email: string): boolean {
      const regex = /^[^\s@'"&]+@[^\s@'"&]+\.[^\s@'"&]+$/
      return regex.test(email.toLowerCase())
    },

    inviteEnseignant(): void {
      this.infoReportingService.dialogWithInput(
        this.$t(
          "projetPage.infoAdministratives.participants.modaleAjout.titreEnseignant",
        ).toString(),
        this.$t("projetPage.infoAdministratives.participants.modaleAjout.message").toString(),
        {
          type: "email",
          placeholder: this.$t(
            "projetPage.infoAdministratives.participants.modaleAjout.emailPlaceholder",
          ).toString(),
        },
        this.$t("projetPage.infoAdministratives.participants.modaleAjout.valider").toString(),
        (courriel: string) => {
          if (this.isValidEmail(courriel)) {
            this.addEnseignant(courriel)
          } else {
            this.infoReportingService.warn("Invalid email format", new Error())
          }
        },
        "",
      )
    },

    async addEnseignant(courriel: string): Promise<void> {
      if (!this.alreadyParticipating(courriel)) {
        if (await this.hasExpectedProfilAndGrae(courriel, PROFIL_UTILISATEUR.ENSEIGNANT)) {
          const updatedProjetAnnuel = {
            ...this.projetAnnuel,
            enseignants: [
              ...this.projetAnnuel.enseignants,
              { courriel: courriel } as UtilisateurDTO,
            ],
            charteFileValid: false,
          }
          this.$emit("projet-annuel-updated", updatedProjetAnnuel)
          this.invitations = [...this.invitations, courriel]
          this.dirtifyProject()
        }
      }
    },

    inviteReferent(): void {
      if (this.projetAnnuel.referents.length) {
        this.$buefy.dialog.confirm({
          message: this.$t(
            "projetPage.infoAdministratives.participants.AddReferentConfirm",
          ).toString(),
          confirmText: this.$t("confirmer").toString(),
          cancelText: this.$t("annuler").toString(),
          onConfirm: () => this.showReferentDialog(),
        })
      } else {
        this.showReferentDialog()
      }
    },

    showReferentDialog(): void {
      this.infoReportingService.dialogWithInput(
        this.$t("projetPage.infoAdministratives.participants.modaleAjout.titreReferent").toString(),
        this.$t("projetPage.infoAdministratives.participants.modaleAjout.message").toString(),
        {
          type: "email",
          placeholder: this.$t(
            "projetPage.infoAdministratives.participants.modaleAjout.emailPlaceholder",
          ).toString(),
        },
        this.$t("projetPage.infoAdministratives.participants.modaleAjout.valider").toString(),
        (courriel: string) => {
          this.addReferent(courriel)
        },
        "",
      )
    },

    async addReferent(courriel: string): Promise<void> {
      if (!this.alreadyParticipating(courriel)) {
        if (await this.hasExpectedProfilAndGrae(courriel, PROFIL_UTILISATEUR.REFERENT)) {
          const updatedProjetAnnuel = {
            ...this.projetAnnuel,
            referents: [...this.projetAnnuel.referents, { courriel: courriel } as UtilisateurDTO],
            charteFileValid: false,
          }
          this.$emit("projet-annuel-updated", updatedProjetAnnuel)
          this.invitations = [...this.invitations, courriel]
          this.dirtifyProject()
        }
      }
    },

    alreadyParticipating(courriel: string): boolean {
      let allParticipantsCourriel = this.projetAnnuel.enseignants.map((ens) => ens.courriel)
      allParticipantsCourriel = allParticipantsCourriel.concat(
        this.projetAnnuel.referents.map((ref) => ref.courriel),
      )
      const alreadyParticipating = allParticipantsCourriel.indexOf(courriel.trim()) > -1
      if (alreadyParticipating) {
        this.infoReportingService.warn(
          this.$t("projetPage.infoAdministratives.participants.already-in-project").toString(),
          new Error(),
        )
        return true
      } else {
        return false
      }
    },

    async hasExpectedProfilAndGrae(
      courriel: string,
      expectedProfil: ProfilUtilisateur,
    ): Promise<boolean> {
      const grae = this.loggedUser.grae
      const hasExpectedProfilAndGrae = await this.usersService.checkProfilAndGrae(
        courriel,
        expectedProfil,
        grae,
      )
      if (!hasExpectedProfilAndGrae) {
        this.infoReportingService.warn(
          this.$t("projetPage.infoAdministratives.participants.wrong-profile", [
            this.$t(expectedProfil).toString(),
          ]).toString(),
          new Error(),
        )
      }
      return hasExpectedProfilAndGrae
    },

    updateProjetAnnuel(updatedProjetAnnuel: any): void {
      this.$emit("projet-annuel-updated", updatedProjetAnnuel)
      // Déclencher la validation pour mettre à jour le menu contextuel
      this.$nextTick(() => this.$emit("trigger-validation"))
    },

    inviteToJoinEnseignant(enseignant: UtilisateurDTO): void {
      const updatedEnseignants = [...this.projetAnnuel.enseignants, enseignant]
      const updatedProjetannuel = {
        ...this.projetAnnuel,
        enseignants: updatedEnseignants,
        charteFileValid: false,
      }

      this.addInvitation(enseignant.courriel)
      this.updateProjetAnnuel(updatedProjetannuel)
    },

    inviteToJoinReferent(referent: UtilisateurDTO): void {
      const updatedReferents = [...this.projetAnnuel.referents, referent]
      const updatedProjetannuel = {
        ...this.projetAnnuel,
        referents: updatedReferents,
        charteFileValid: false,
      }

      this.addInvitation(referent.courriel)
      this.updateProjetAnnuel(updatedProjetannuel)
    },

    addInvitation(courriel: string): void {
      this.invitations = [...this.invitations, courriel]
    },

    updateEnseignants(updatedEnseignants: UtilisateurDTO[]): void {
      const updatedProjetAnnuel = {
        ...this.projetAnnuel,
        enseignants: updatedEnseignants,
      }
      this.$emit("projet-annuel-updated", updatedProjetAnnuel)
      // Déclencher la validation pour mettre à jour le menu contextuel
      this.$nextTick(() => this.$emit("trigger-validation"))
    },

    updateConsentementEnAttente(updatedConsentementEnAttente: string[]): void {
      const updatedProjetAnnuel = {
        ...this.projetAnnuel,
        consentementEnAttente: updatedConsentementEnAttente,
      }
      this.$emit("projet-annuel-updated", updatedProjetAnnuel)
      // Déclencher la validation pour mettre à jour le menu contextuel
      this.$nextTick(() => this.$emit("trigger-validation"))
    },

    openProfileModale(payload: { show: boolean; id: string }): void {
      eventBus.emit("open-profile-modale", payload)
    },
  },
})
</script>

<style lang="scss">
@import "@/styles/variables";

.textarea:not([rows]) {
  min-height: 5em !important;
}

.participating {
  color: $green-v2;
}

.invited {
  color: $green-v2;
}

#app {
  .projet-part {
    .modal-card-title {
      color: $ofb-bleu-1;
    }

    .b-table .table-wrapper.has-sticky-header {
      min-height: 0;
    }
  }
}

.projet-part {
  .is-mandatory {
    color: $dark;
    width: 100%;
    font-weight: bold;
    display: flex;
    justify-content: flex-end;
  }
}
</style>
